// ==================== PART 1: IMPORTS AND SETUP ====================
// File: STCarePlanForm.tsx
// Instructions: This is Part 1 of 6. Copy all parts in order and combine into one file.

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  AlertCircle, MessageCircle, User, ClipboardList, Clock,
  Baby, Heart, Shield, Truck, Stethoscope, Brain, Utensils, Volume2,
  ChevronDown, ChevronUp, ChevronsDown, ChevronsUp, Home, Activity
} from 'lucide-react';
import { FormPageLayout, DocStatusPill } from '../../common';
import type { ActionButtonConfig } from '../../common';
import { CarePlanStatus } from '../../../services/patientmr/careplans.service';
import { useCarePlanDraft } from './hooks/useCarePlanDraft';
import { useFeature } from '../../../hooks/useFeature';
import GoalsEditor from './goals/GoalsEditor';
import OrderingPhysicianSection from './sections/OrderingPhysicianSection';
import CertPeriodSection, { applyCertPeriod, hasFixedCertPeriod } from './sections/CertPeriodSection';
import type { CarePlanCertificationPeriod } from '../../../dtos/patientmr/careplans/careplan.dto';
import HomeboundSection, { homeboundJustificationField } from './sections/HomeboundSection';
import MedicalNecessitySection from './sections/MedicalNecessitySection';
import ProviderSection from './sections/ProviderSection';
import type { SectionFieldConfig } from './sections/SectionCard';
import { CarePlanGoal, initialGoals, clearedLegacyGoalFields, goalErrors } from './goals/carePlanGoals';
import { AssessmentsService, AssessmentListItem, AssessmentDiscipline } from '../../../services/patientmr/assessments.service';

interface STCarePlanFormProps {
  editingId?: string | null;
  patientId?: string;
  existingData?: any;
  assessmentId?: string;
  assessmentData?: any;
  onSave: (data: any, submitAfterSave?: boolean) => void;
  onSubmit?: () => void;
  onReopen?: () => void;
  onApprove?: () => void;
  onCancel: () => void;
  saving?: boolean;
  submitting?: boolean;
  reopening?: boolean;
  carePlanStatus?: CarePlanStatus;
  isReadOnly?: boolean;
  /** Start QA review (SUBMITTED -> QA); shown only when provided */
  onStartQA?: () => void;
  startingQA?: boolean;
  /** Extra header content (version badge, version picker) */
  headerExtra?: React.ReactNode;
  /** Saves a draft without closing the form (autosave) */
  onAutosave?: (data: Record<string, unknown>) => Promise<void>;
  /** Autosave runs only while true */
  autosaveEnabled?: boolean;
  /** Phase 19 F26: the plan record's certification period; its dates are shown read-only */
  certificationPeriod?: CarePlanCertificationPeriod | null;
}

type AssessmentType = 'initial' | 'recertification' | 'discharge';

interface CheckboxFieldProps {
  field: string;
  label: string;
  checked: boolean;
  onChange: (field: string, value: boolean) => void;
  isReadOnly?: boolean;
}

const CheckboxField = React.memo(({ field, label, checked, onChange, isReadOnly = false }: CheckboxFieldProps) => (
  <label className={`flex items-center space-x-2 text-sm text-gray-700 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}>
    <input type="checkbox" checked={checked}
      onChange={(e) => onChange(field, e.target.checked)}
      disabled={isReadOnly}
      className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`} />
    <span>{label}</span>
  </label>
));

// Phase 19 F25: per-plan copy for the shared sections (keys unchanged).
const ST_PHYSICIAN_OVERRIDES = {
  orderingPhysicianName: { label: 'Ordering Physician Name', placeholder: 'Dr. Sarah Chen, MD' },
  orderingPhysicianPhone: { placeholder: '(713) 555-2000' },
  orderingPhysicianFax: { placeholder: '(713) 555-2001' },
  orderingPhysicianSpecialty: { placeholder: 'Pediatric Pulmonology' },
  orderingPhysicianAddressStreet: { label: 'Street Address', placeholder: '6621 Fannin Street, Suite 1234' },
  orderingPhysicianAddressCity: { placeholder: 'Houston' },
  orderingPhysicianAddressState: { placeholder: 'TX' },
  orderingPhysicianAddressZip: { label: 'ZIP Code', placeholder: '77030' },
};

const ST_HOMEBOUND_CRITERIA = [
  'Patient leaves home infrequently and with considerable and taxing effort',
  'Patient requires supportive devices',
  'Patient requires special transportation',
  'Patient requires assistance of another person',
  'Normal inability to leave home due to medical condition',
  'Physician has determined leaving home is medically contraindicated',
];

const ST_HOMEBOUND_FIELDS: SectionFieldConfig[] = [
  { key: 'homeboundStatusCriteria', label: 'Homebound Status Criteria (Check all that apply)', kind: 'checklist', required: true, wide: true,
    options: ST_HOMEBOUND_CRITERIA.map(v => ({ value: v, label: v })) },
  { key: 'homeboundSupportiveDevices', label: 'Supportive Devices Required', placeholder: 'walker, wheelchair, oxygen, ventilator, AAC device' },
  { key: 'homeboundSpecialTransportation', label: 'Special Transportation', placeholder: 'Medical van, stretcher transport, ventilator-equipped' },
  { key: 'homeboundAssistanceRequired', label: 'Assistance Required', kind: 'textarea', rows: 2, wide: true,
    placeholder: 'Supervision, physical assist, continuous skilled nursing, cannot be left unattended' },
  homeboundJustificationField({ rows: 3, placeholder: 'Provide detailed justification for homebound status' }),
];

const ST_MEDICAL_NECESSITY_FIELDS: SectionFieldConfig[] = [
  { key: 'medicalNecessityClinicalComplexity', label: 'Clinical Complexity', kind: 'textarea', required: true,
    placeholder: "Describe patient's medical condition and complexity affecting communication/swallowing. Include: Primary diagnosis/comorbidities, Medical history, Why this requires skilled ST, Technology dependence" },
  { key: 'medicalNecessitySkilledCareRequirements', label: 'Skilled Care Requirements', kind: 'textarea', required: true,
    placeholder: 'Explain WHY skilled ST required: Specific techniques needed, Clinical judgment required, Safety monitoring, Why caregiver/aide cannot provide this' },
  { key: 'medicalNecessityFunctionalImpact', label: 'Functional Impact', kind: 'textarea', required: true,
    placeholder: 'How communication/swallowing impairments affect function: Communication limitations, Feeding safety risks, ADL impact, Safety risks, Caregiver burden' },
  { key: 'medicalNecessityExpectedOutcomes', label: 'Expected Outcomes', kind: 'textarea', required: true,
    placeholder: 'What functional improvements expected with ST? Measurable outcomes, Timeframe, How therapy reduces hospitalizations/complications' },
];

const STCarePlanForm: React.FC<STCarePlanFormProps> = ({
  editingId, patientId, existingData, assessmentId, assessmentData,
  onSave, onReopen, onApprove, onCancel,
  saving = false, submitting = false, reopening = false,
  carePlanStatus = CarePlanStatus.DRAFT,
  isReadOnly = false,
  onStartQA,
  startingQA = false,
  headerExtra,
  onAutosave,
  autosaveEnabled = false,
  certificationPeriod = null
}) => {
  const medicareEnabled = useFeature('medicareContent');
  const [formConfig, setFormConfig] = useState({
    patientType: 'pediatric' as 'pediatric' | 'adult',
    settingType: 'home-health' as 'home-health' | 'outpatient',
    isPDN: true,
    assessmentType: 'initial' as AssessmentType,
    primaryFocus: 'speech-language' as 'speech-language' | 'swallowing' | 'cognitive' | 'voice',
  });

  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    config: false, assessmentRef: false, orderingPhysician: true, certification: true,
    clinicalStatus: true, mentalStatus: true, functionalLimitations: true, activitiesPermitted: true,
    homebound: true, physicianOrders: true, goals: true, pediatricLanguage: true,
    pdnConsiderations: true, swallowing: true, aac: true, caregiverTraining: true,
    detailedGoals: true, interventions: true, frequency: true, dme: true,
    safety: true, coordination: true, discharge: true, medicalNecessity: true,
    provider: true, additionalNotes: true
  });

  const allSectionsCollapsed = useMemo(
    () => Object.values(collapsedSections).every(v => v === true),
    [collapsedSections]
  );

  const toggleSection = useCallback((section: string) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const toggleAllSections = useCallback(() => {
    const newState = !allSectionsCollapsed;
    const newSections: Record<string, boolean> = {};
    Object.keys(collapsedSections).forEach(key => { newSections[key] = newState; });
    setCollapsedSections(newSections);
  }, [allSectionsCollapsed, collapsedSections]);

  const [formData, setFormData] = useState({
    // Phase 19 F12: every goal of the plan (domain, measure, target date, status, progress)
    goals: [] as CarePlanGoal[],
    assessmentId: '', assessmentDate: '', assessedBy: '', assessmentType: '',
    orderingPhysicianName: '', orderingPhysicianNPI: '', orderingPhysicianPhone: '',
    orderingPhysicianFax: '', orderingPhysicianSpecialty: '',
    orderingPhysicianAddressStreet: '', orderingPhysicianAddressCity: '',
    orderingPhysicianAddressState: '', orderingPhysicianAddressZip: '',
    certificationPeriodFrom: '', certificationPeriodTo: '',
    prognosis: '', rehabilitationPotential: '', rehabilitationPotentialRationale: '',
    mentalStatusIndicators: [] as string[], mentalStatusOrientationLevel: '',
    mentalStatusDescription: '', mentalStatusCognitiveLevelPediatric: '',
    functionalLimitationAmbulation: false, functionalLimitationAmputation: false,
    functionalLimitationBowelBladder: false, functionalLimitationContracture: false,
    functionalLimitationHearing: false, functionalLimitationParalysis: false,
    functionalLimitationEndurance: false, functionalLimitationLegallyBlind: false,
    functionalLimitationDyspnea: false, functionalLimitationSpeech: false,
    functionalLimitationLanguage: false, functionalLimitationSwallowing: false,
    functionalLimitationCognitive: false, functionalLimitationVoice: false,
    functionalLimitationFluency: false, functionalLimitationAAC: false,
    functionalLimitationOther: '',
    activitiesPermittedLevel: [] as string[], activitiesPermittedSpecialPrecautions: '',
    homeboundStatusCriteria: [] as string[], homeboundSupportiveDevices: '',
    homeboundSpecialTransportation: '', homeboundAssistanceRequired: '', homeboundJustification: '',
    verbalOrderReceived: '', verbalOrderDate: '', verbalOrderReceivedBy: '',
    writtenPOCSentDate: '', writtenPOCSentMethod: '', physicianReceivedPOCDate: '',
    physicianSignatureStatus: '', physicianSignedDate: '',
    faceToFaceRequired: '', faceToFaceDate: '', faceToFaceType: '', faceToFaceClinicianName: '',
    planCreatedBy: '', planCreationDate: '',
    shortTermGoals: '', longTermGoals: '',
    pediatricChronologicalAge: '', pediatricAdjustedAge: '',
    pediatricReceptiveLanguageAge: '', pediatricExpressiveLanguageAge: '',
    pediatricCurrentCommunicationLevel: '',
    pediatricLanguageGoal1: '', pediatricLanguageGoal2: '', pediatricLanguageGoal3: '',
    pediatricSpeechGoal: '', pediatricPragmaticGoal: '', pediatricFeedingGoal: '', pediatricAACGoal: '',
    pdnTracheostomyStatus: '', pdnSpeakingValveStatus: '', pdnVentilatorStatus: '',
    pdnMedicalStabilityForST: '', pdnSTSessionPrecautions: '',
    pdnCommunicationWithTracheostomy: '',
    pdnCurrentFeedingMethod: '', pdnOralFeedingStatus: '', pdnDietTexture: '',
    pdnLiquidConsistency: '', pdnAspirationRisk: '', pdnSwallowingPrecautions: '',
    pdnSwallowingGoal: '', pdnSwallowingIntervention: '',
    pdnAACStatus: '', pdnCurrentAACSystem: '', pdnAACAccessMethod: '',
    pdnAACTrainingNeeds: '', pdnAACGoal: '', pdnAACIntervention: '',
    pdnSTCaregiverName: '', pdnSTCaregiverTrainingStatus: '',
    pdnSTCaregiverCompetencies: '',
    pdnLanguageStimulationTraining: '', pdnFeedingStrategyTraining: '',
    pdnAACUseTraining: '',
    goalReceptiveLanguage: '', goalExpressiveLanguage: '', goalSpeechProduction: '',
    goalSwallowing: '', goalCognitiveLanguage: '', goalVoice: '', goalFluency: '',
    goalPragmatics: '', goalAAC: '', goalCaregiverEducation: '', goalOther: '',
    interventionSpeechLanguage: '', interventionSwallowingDysphagia: '', interventionOther: '',
    stVisitsPerWeek: '', stMinutesPerVisit: '', stTotalHoursPerWeek: '',
    stStartDate: '', stAnticipatedEndDate: '', stTotalDays: '',
    stCertificationPeriod: '', stFrequencyJustification: '',
    dmeAACDevice: false, dmeAACDeviceType: '', dmeCommunicationBoard: false,
    dmeSpeakingValve: false, dmeSpeakingValveType: '', dmeThickeningAgents: false,
    dmeAdaptedUtensils: false, dmeOther: '', dmeSupplier: '',
    safetyAspirationPrecautions: false, safetyAspirationPrecautionsDetails: '',
    safetySeizurePrecautions: false, safetyTracheostomyPrecautions: false,
    safetySupervisedFeeding: false, safetyEmergencyPlanReviewed: false, safetyOther: '',
    coordinationWithNursing: '', coordinationWithPT: '', coordinationWithOT: '',
    coordinationWithENT: '',
    coordinationWithSchool: '',
    dischargeGoals: '', dischargeCriteria: '',
    schoolTransitionPlan: '',
    followUpRecommendations: '',
    medicalNecessityClinicalComplexity: '', medicalNecessitySkilledCareRequirements: '',
    medicalNecessityFunctionalImpact: '', medicalNecessityExpectedOutcomes: '',
    providerName: '', providerCredentials: '', providerNPI: '',
    supervisingPhysician: '', supervisingPhysicianNPI: '',
    dateCreated: new Date().toISOString().split('T')[0],
    additionalNotes: ''
  });

  const [assessmentsList, setAssessmentsList] = useState<AssessmentListItem[]>([]);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const selectRef = useRef<HTMLSelectElement>(null);
  const hasLoadedExistingData = useRef(false);

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (assessmentId && assessmentData) {
      setFormData(prev => ({
        ...prev,
        assessmentId, 
        assessmentDate: assessmentData.assessmentDate || '',
        assessedBy: assessmentData.assessedBy || ''
      }));
    }
  }, [assessmentId, assessmentData]);

  useEffect(() => {
    if (existingData && !hasLoadedExistingData.current) {
      setFormData(prev => ({ ...prev, ...existingData, goals: initialGoals('ST', existingData) }));
      if (existingData.formConfig) setFormConfig(existingData.formConfig);
      hasLoadedExistingData.current = true;
    }
  }, [existingData]);

  // Phase 19 F26: a plan with a certification period stores its dates in the cert period keys
  useEffect(() => {
    if (hasFixedCertPeriod(certificationPeriod)) setFormData(prev => applyCertPeriod(prev, certificationPeriod));
  }, [certificationPeriod]);

  useEffect(() => {
    if (!editingId && !formData.planCreatedBy) {
      setFormData(prev => ({
        ...prev,
        planCreatedBy: prev.providerName || '',
        planCreationDate: new Date().toISOString().split('T')[0]
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- auto-fill only on open/provider change; adding the field would re-fill it after the user clears it
  }, [editingId, formData.providerName]);

  useEffect(() => {
    const visits = parseInt(formData.stVisitsPerWeek || '0');
    const minutes = parseInt(formData.stMinutesPerVisit || '0');
    if (visits > 0 && minutes > 0) {
      setFormData(prev => ({ ...prev, stTotalHoursPerWeek: `${((visits * minutes) / 60).toFixed(1)} hours` }));
    }
  }, [formData.stVisitsPerWeek, formData.stMinutesPerVisit]);

  useEffect(() => {
    const start = formData.stStartDate;
    const end = formData.stAnticipatedEndDate;
    if (start && end) {
      const days = Math.floor((new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60 * 60 * 24));
      setFormData(prev => ({ ...prev, stTotalDays: `${days} days` }));
    }
  }, [formData.stStartDate, formData.stAnticipatedEndDate]);

  const handleConfigChange = (key: string, value: boolean | string) => {
    setFormConfig(prev => ({ ...prev, [key]: value }));
  };

  const loadAssessments = async () => {
    if (assessmentsList.length > 0) return;
    
    try {
      setLoadingAssessments(true);
      const result = await AssessmentsService.getAssessments(patientId);
      // Filter based on discipline: ST
      const disciplineAssessments = result.assessments.filter(
        assessment => assessment.discipline === AssessmentDiscipline.ST
      );
      setAssessmentsList(disciplineAssessments);
    } catch (error) {
      console.error('Error loading assessments:', error);
    } finally {
      setLoadingAssessments(false);
    }
  };

  const formatAssessmentType = (assessmentType: string | undefined): string => {
    if (!assessmentType) return 'N/A';
    
    const typeMap: Record<string, string> = {
      'START_OF_CARE': 'Start of Care',
      'INITIAL': 'Start of Care',
      'initial': 'Start of Care',
      'REASSESSMENT': 'Recertification',
      'REASSESSMEMT': 'Recertification',
      'recertification': 'Recertification',
      'END_OF_CARE': 'Discharge',
      'discharge': 'Discharge'
    };
    
    return typeMap[assessmentType] || assessmentType;
  };

  const handleAssessmentSelection = (assessmentId: string) => {
    const selectedAssessment = assessmentsList.find(a => a.assessmentId === assessmentId);
    
    if (selectedAssessment) {
      setFormData(prev => ({
        ...prev,
        assessmentId: assessmentId,
        assessmentDate: selectedAssessment.date ? new Date(selectedAssessment.date).toISOString().split('T')[0] : '',
        assessedBy: selectedAssessment.providerName || '',
        assessmentType: selectedAssessment.assessmentType || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        assessmentId: assessmentId,
        assessmentDate: '',
        assessedBy: '',
        assessmentType: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    Object.assign(newErrors, goalErrors(formData.goals));
    if (!formData.certificationPeriodFrom) newErrors.certificationPeriodFrom = 'Required';
    if (!formData.certificationPeriodTo) newErrors.certificationPeriodTo = 'Required';
    if (!formData.prognosis) newErrors.prognosis = 'Required';
    if (!formData.rehabilitationPotential) newErrors.rehabilitationPotential = 'Required';
    if (!formData.rehabilitationPotentialRationale.trim()) newErrors.rehabilitationPotentialRationale = 'Required';
    if (formData.mentalStatusIndicators.length === 0) newErrors.mentalStatusIndicators = 'At least one required';
    if (!formData.mentalStatusDescription.trim()) newErrors.mentalStatusDescription = 'Required';
    if (!formData.interventionSpeechLanguage.trim() && !formData.interventionSwallowingDysphagia.trim()) newErrors.interventionSpeechLanguage = 'At least one intervention required';
    if (!formData.stVisitsPerWeek) newErrors.stVisitsPerWeek = 'Required';
    if (!formData.stMinutesPerVisit) newErrors.stMinutesPerVisit = 'Required';
    if (!formData.stStartDate) newErrors.stStartDate = 'Required';
    if (!formData.stAnticipatedEndDate) newErrors.stAnticipatedEndDate = 'Required';
    if (!formData.stCertificationPeriod) newErrors.stCertificationPeriod = 'Required';
    if (!formData.providerName.trim()) newErrors.providerName = 'Required';
    
    if (formConfig.settingType === 'home-health' && formConfig.assessmentType !== 'discharge') {
      if (formData.homeboundStatusCriteria.length === 0) newErrors.homeboundStatusCriteria = 'At least one homebound criterion required';
    }
    
    const verbalDate = formData.verbalOrderDate;
    const reviewDate = formData.physicianReceivedPOCDate;
    if (verbalDate && reviewDate) {
      const days = Math.floor((new Date(reviewDate).getTime() - new Date(verbalDate).getTime()) / (1000 * 60 * 60 * 24));
      if (days > 14) newErrors.physicianReceivedPOCDate = 'WARNING: Must be within 14 days of verbal order';
    }
    
    const medNecTotal = (formData.medicalNecessityClinicalComplexity || '').length +
      (formData.medicalNecessitySkilledCareRequirements || '').length +
      (formData.medicalNecessityFunctionalImpact || '').length +
      (formData.medicalNecessityExpectedOutcomes || '').length;
    if (medNecTotal < 500 && formConfig.assessmentType !== 'discharge') {
      newErrors.medicalNecessityClinicalComplexity = 'Medical necessity too brief (min 500 chars across all sections)';
    }
    
    const funcLimits = [
      formData.functionalLimitationSpeech, formData.functionalLimitationLanguage,
      formData.functionalLimitationSwallowing, formData.functionalLimitationAmbulation
    ].filter(Boolean);
    if (funcLimits.length === 0) newErrors.functionalLimitationSpeech = 'At least one functional limitation required';
    
    if (formConfig.patientType === 'pediatric' && formConfig.isPDN) {
      if (!formData.pdnMedicalStabilityForST) newErrors.pdnMedicalStabilityForST = 'Required';
      if (!formData.stFrequencyJustification.trim()) newErrors.stFrequencyJustification = 'Required for Medicaid';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = (submitAfterSave = false) => {
    if (!submitAfterSave || validateForm()) onSave({ ...formData, ...clearedLegacyGoalFields('ST'), goals: formData.goals, formConfig, status: 'Draft' }, submitAfterSave);
  };

  const handleChange = useCallback((field: string, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: '' };
    });
  }, []);

  // getStatusColor / getStatusDisplayText removed — use <DocStatusPill /> from common/Pills

  const isPediatric = formConfig.patientType === 'pediatric';
  const isPediatricPDN = isPediatric && formConfig.isPDN;
  const isHomeHealth = formConfig.settingType === 'home-health';

  const isAnyLoading = saving || submitting || reopening || startingQA;
  const isDraft = !carePlanStatus || carePlanStatus === CarePlanStatus.DRAFT;
  const isSubmittedOrQA = carePlanStatus === CarePlanStatus.SUBMITTED || carePlanStatus === CarePlanStatus.QA;

  // Phase 4 (ASM-03): dirty tracking, autosave while DRAFT, unsaved-changes guard
  const draft = useCarePlanDraft({
    getSnapshot: () => JSON.stringify({ formData, formConfig }),
    getSaveData: () => ({ ...formData, ...clearedLegacyGoalFields('ST'), goals: formData.goals, formConfig, status: 'Draft' }),
    onAutosave,
    enabled: autosaveEnabled && isDraft && !isReadOnly && !!editingId,
    resetKey: `${editingId ?? 'new'}:${carePlanStatus}`,
  });
  const handleClose = () => draft.confirmLeave(onCancel);
  const isComplete = carePlanStatus === CarePlanStatus.COMPLETE;

  const actions: ActionButtonConfig[] = [
    { key: 'cancel', label: 'Close', onClick: handleClose, variant: 'secondary', disabled: isAnyLoading },
    { key: 'save', label: 'Save', loadingLabel: editingId ? 'Updating...' : 'Saving...', onClick: () => handleSave(false), variant: 'primary', disabled: isAnyLoading, loading: saving, hidden: !isDraft || isReadOnly },
    { key: 'submit', label: 'Submit', loadingLabel: 'Submitting...', onClick: () => handleSave(true), variant: 'primary', disabled: isAnyLoading, loading: submitting, hidden: !isDraft || isReadOnly },
    { key: 'reject', label: 'Reject', loadingLabel: 'Rejecting...', onClick: onReopen ?? (() => {}), variant: 'danger', disabled: isAnyLoading, loading: reopening, hidden: !isSubmittedOrQA || !onReopen || isReadOnly },
    { key: 'startQA', label: 'Start QA', loadingLabel: 'Starting QA...', onClick: onStartQA ?? (() => {}), variant: 'warning', disabled: isAnyLoading, loading: startingQA, hidden: carePlanStatus !== CarePlanStatus.SUBMITTED || !onStartQA || isReadOnly },
    { key: 'approve', label: 'Approve', loadingLabel: 'Approving...', onClick: onApprove ?? (() => {}), variant: 'success', disabled: isAnyLoading, loading: submitting, hidden: !isSubmittedOrQA || !onApprove || isReadOnly },
    { key: 'reopen', label: 'Reopen', loadingLabel: 'Reopening...', onClick: onReopen ?? (() => {}), variant: 'primary', disabled: isAnyLoading, loading: reopening, hidden: !isComplete || !onReopen || isReadOnly, className: 'bg-blue-600 hover:bg-blue-700' },
  ];

  return (
    <FormPageLayout
      onBack={handleClose}
      actions={actions}
      title="Speech Therapy Care Plan"
      headerRight={
        <div className="flex items-center gap-2">
          {draft.indicator}
          {headerExtra}
          <DocStatusPill status={carePlanStatus} />
          <button
            type="button"
            onClick={toggleAllSections}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md border border-slate-200 transition-colors"
          >
            {allSectionsCollapsed ? (
              <><ChevronsDown size={14} /><span>Expand All</span></>
            ) : (
              <><ChevronsUp size={14} /><span>Collapse All</span></>
            )}
          </button>
        </div>
      }
    >
      <div {...draft.captureProps}>
          {/* ========== FORM CONFIGURATION ========== */}
          <div className="bg-gray-50 rounded-lg border border-gray-200">
            <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => toggleSection('config')}>
              <h4 className="text-md font-medium text-gray-900 flex items-center">
                <ClipboardList size={18} className="mr-2" />Care Plan Configuration
              </h4>
              {collapsedSections.config ? <ChevronDown className="text-gray-900" /> : <ChevronUp className="text-gray-900" />}
            </div>
            <div className={collapsedSections.config ? 'hidden' : 'p-4 border-t border-gray-300'}>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Patient Type</label>
                  <select disabled={isReadOnly} className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} value={formConfig.patientType} onChange={(e) => handleConfigChange('patientType', e.target.value)}>
                    <option value="pediatric">Pediatric</option><option value="adult">Adult</option>
                  </select>
                </div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Setting</label>
                  <select disabled={isReadOnly} className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} value={formConfig.settingType} onChange={(e) => handleConfigChange('settingType', e.target.value)}>
                    <option value="home-health">Home Health</option><option value="outpatient">Outpatient</option>
                  </select>
                </div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Assessment Type</label>
                  <select disabled={isReadOnly} className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} value={formConfig.assessmentType} onChange={(e) => handleConfigChange('assessmentType', e.target.value)}>
                    <option value="initial">Initial Evaluation</option><option value="recertification">Recertification</option><option value="discharge">Discharge</option>
                  </select>
                </div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Primary Focus</label>
                  <select disabled={isReadOnly} className={`w-full p-2 border border-gray-300 rounded text-base ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} value={formConfig.primaryFocus} onChange={(e) => handleConfigChange('primaryFocus', e.target.value)}>
                    <option value="speech-language">Speech-Language</option><option value="swallowing">Swallowing/Dysphagia</option><option value="cognitive">Cognitive-Communication</option><option value="voice">Voice</option>
                  </select>
                </div>
              </div>
              <div className="mt-4 flex items-center">
                <label className="flex items-center text-sm text-gray-600">
                  <input type="checkbox" className={`mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={isReadOnly}
                    checked={formConfig.isPDN} onChange={(e) => handleConfigChange('isPDN', e.target.checked)} />PDN Patient
                </label>
              </div>
              {isPediatricPDN && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start">
                  <MessageCircle size={18} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <strong>Pediatric PDN ST Care Plan:</strong> Includes language development, swallowing/feeding, AAC, and caregiver training sections required for Texas Medicaid PDN authorization.
                  </div>
                </div>
              )}

              {/* ST Assessment Reference */}
              <div className="mt-6">
                <h5 className="text-sm font-medium text-gray-700 mb-3">Assessment Reference</h5>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Speech Therapy Assessment *</label>
                    <select
                      ref={selectRef}
                      value={formData.assessmentId}
                      onChange={(e) => handleAssessmentSelection(e.target.value)}
                      onFocus={loadAssessments}
                      className={`w-full px-2 py-2 text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${errors.assessmentId ? 'border-red-500' : 'border-gray-300'} max-w-full ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                      disabled={loadingAssessments || isReadOnly}
                    >
                      <option value="">{loadingAssessments ? 'Loading ST assessments...' : 'Select an ST assessment'}</option>
                      {assessmentsList.map((assessment) => (
                        <option key={assessment.assessmentId} value={assessment.assessmentId}>
                          {formatAssessmentType(assessment.assessmentType)} | {new Date(assessment.date).toLocaleDateString()} | {assessment.providerName}
                        </option>
                      ))}
                    </select>
                    {errors.assessmentId && <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" />{errors.assessmentId}</p>}
                  </div>
                  
                  {/* Selected Assessment Details */}
                  {formData.assessmentId && (
                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <h6 className="text-sm font-semibold text-orange-900 mb-2">Selected Assessment Details</h6>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Assessment ID:</span>
                          <p className="text-gray-900 mt-0.5">{formData.assessmentId}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Type:</span>
                          <p className="text-gray-900 mt-0.5">
                            {formatAssessmentType(formData.assessmentType)}
                          </p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Assessment Date:</span>
                          <p className="text-gray-900 mt-0.5">
                            {formData.assessmentDate ? new Date(formData.assessmentDate).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Assessed By:</span>
                          <p className="text-gray-900 mt-0.5">{formData.assessedBy || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-2">Note: Diagnosis information is pulled from the ST Assessment. To update diagnoses, edit the linked assessment.</p>
              </div>

              {/* Certification Period */}
              <CertPeriodSection fixedPeriod={certificationPeriod} title="Certification Period (CMS-485 Sections 2-3)" values={formData} onChange={handleChange} errors={errors} readOnly={isReadOnly} />
            </div>
          </div>

          {/* ========== ORDERING PHYSICIAN ========== */}
          <OrderingPhysicianSection
            title="Ordering Physician (CMS-485 Section 17)"
            tone="red"
            overrides={ST_PHYSICIAN_OVERRIDES}
            values={formData}
            onChange={handleChange}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={collapsedSections.orderingPhysician}
            onToggle={() => toggleSection('orderingPhysician')}
          />

          {/* ========== CLINICAL STATUS ========== */}
          <div className="bg-amber-50 rounded-lg border border-amber-200">
            <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => toggleSection('clinicalStatus')}>
              <h4 className="text-md font-medium text-amber-900 flex items-center">
                <ClipboardList size={18} className="mr-2 text-amber-600" />Clinical Status (CMS-485 Section 11)
              </h4>
              {collapsedSections.clinicalStatus ? <ChevronDown className="text-amber-900" /> : <ChevronUp className="text-amber-900" />}
            </div>
            <div className={collapsedSections.clinicalStatus ? 'hidden' : 'p-4 border-t border-amber-300'}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Prognosis *</label>
                  <select value={formData.prognosis} onChange={(e) => handleChange('prognosis', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md ${errors.prognosis ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select</option><option value="Poor">Poor</option><option value="Guarded">Guarded</option><option value="Fair">Fair</option><option value="Good">Good</option><option value="Excellent">Excellent</option>
                  </select>
                  {errors.prognosis && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.prognosis}</p>}
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Rehabilitation Potential *</label>
                  <select value={formData.rehabilitationPotential} onChange={(e) => handleChange('rehabilitationPotential', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md ${errors.rehabilitationPotential ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select</option><option value="Excellent">Excellent</option><option value="Good">Good</option><option value="Fair">Fair</option><option value="Guarded">Guarded</option><option value="Poor">Poor</option>
                  </select>
                  {errors.rehabilitationPotential && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.rehabilitationPotential}</p>}
                </div>
                <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Rehabilitation Potential Rationale *</label>
                  <textarea value={formData.rehabilitationPotentialRationale} onChange={(e) => handleChange('rehabilitationPotentialRationale', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md ${errors.rehabilitationPotentialRationale ? 'border-red-500' : 'border-gray-300'} '' ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                    placeholder="Explain rationale based on patient's condition, cognitive status, caregiver support, compliance, medical stability" />
                  {errors.rehabilitationPotentialRationale && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.rehabilitationPotentialRationale}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* ========== MENTAL STATUS ========== */}
          <div className="bg-purple-50 rounded-lg border border-purple-200">
            <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => toggleSection('mentalStatus')}>
              <h4 className="text-md font-medium text-purple-900 flex items-center">
                <Brain size={18} className="mr-2 text-purple-600" />Mental Status (CMS-485 Section 10)
              </h4>
              {collapsedSections.mentalStatus ? <ChevronDown className="text-purple-900" /> : <ChevronUp className="text-purple-900" />}
            </div>
            <div className={collapsedSections.mentalStatus ? 'hidden' : 'p-4 border-t border-purple-300'}>
              <div className="space-y-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-2">Mental Status Indicators (Check all) *</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {['Oriented', 'Disoriented', 'Forgetful', 'Depressed', 'Agitated', 'Comatose'].map((option) => (
                      <label key={option} className="flex items-center space-x-2">
                        <input type="checkbox" className={`rounded border-gray-300 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                          disabled={isReadOnly}
                          checked={formData.mentalStatusIndicators.includes(option)}
                          onChange={(e) => {
                            const current = formData.mentalStatusIndicators || [];
                            const newVals = e.target.checked ? [...current, option] : current.filter(v => v !== option);
                            handleChange('mentalStatusIndicators', newVals);
                          }} />
                        <span className="text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                  {errors.mentalStatusIndicators && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.mentalStatusIndicators}</p>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Orientation Level</label>
                    <select disabled={isReadOnly} value={formData.mentalStatusOrientationLevel} onChange={(e) => handleChange('mentalStatusOrientationLevel', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select</option><option value="Oriented x4">Oriented x4 (person, place, time, situation)</option><option value="Oriented x3">Oriented x3</option><option value="Oriented x2">Oriented x2</option><option value="Oriented x1">Oriented x1</option><option value="Disoriented">Disoriented</option><option value="N/A">N/A - pediatric/unable to assess</option>
                    </select>
                  </div>
                  {isPediatric && (
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Cognitive Level (Pediatric)</label>
                      <input type="text" value={formData.mentalStatusCognitiveLevelPediatric} onChange={(e) => handleChange('mentalStatusCognitiveLevelPediatric', e.target.value)}
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} placeholder="e.g., Functions at 18-24 month level" />
                    </div>
                  )}
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Mental Status Description *</label>
                  <textarea value={formData.mentalStatusDescription} onChange={(e) => handleChange('mentalStatusDescription', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md ${errors.mentalStatusDescription ? 'border-red-500' : 'border-gray-300'} '' ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                    placeholder={isPediatric ? "For pediatric: Describe cognitive/developmental level, responsiveness, ability to follow commands, communication method"
                      : "Describe cognitive function, memory, attention, safety awareness, judgment as it relates to ST"} />
                  {errors.mentalStatusDescription && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.mentalStatusDescription}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* ========== FUNCTIONAL LIMITATIONS ========== */}
          <div className="bg-cyan-50 rounded-lg border border-cyan-200">
            <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => toggleSection('functionalLimitations')}>
              <h4 className="text-md font-medium text-cyan-900 flex items-center">
                <Activity size={18} className="mr-2 text-cyan-600" />Functional Limitations (CMS-485 Section 8)
              </h4>
              {collapsedSections.functionalLimitations ? <ChevronDown className="text-cyan-900" /> : <ChevronUp className="text-cyan-900" />}
            </div>
            <div className={collapsedSections.functionalLimitations ? 'hidden' : 'p-4 border-t border-cyan-300'}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">General Functional Limitations (CMS-485 Required)</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <CheckboxField field="functionalLimitationAmbulation" label="Ambulation" checked={formData.functionalLimitationAmbulation} onChange={handleChange}  isReadOnly={isReadOnly} />
                    <CheckboxField field="functionalLimitationAmputation" label="Amputation" checked={formData.functionalLimitationAmputation} onChange={handleChange}  isReadOnly={isReadOnly} />
                    <CheckboxField field="functionalLimitationBowelBladder" label="Bowel/Bladder (incontinence)" checked={formData.functionalLimitationBowelBladder} onChange={handleChange} />
                    <CheckboxField field="functionalLimitationContracture" label="Contracture" checked={formData.functionalLimitationContracture} onChange={handleChange}  isReadOnly={isReadOnly} />
                    <CheckboxField field="functionalLimitationHearing" label="Hearing" checked={formData.functionalLimitationHearing} onChange={handleChange}  isReadOnly={isReadOnly} />
                    <CheckboxField field="functionalLimitationParalysis" label="Paralysis" checked={formData.functionalLimitationParalysis} onChange={handleChange}  isReadOnly={isReadOnly} />
                    <CheckboxField field="functionalLimitationEndurance" label="Endurance" checked={formData.functionalLimitationEndurance} onChange={handleChange}  isReadOnly={isReadOnly} />
                    <CheckboxField field="functionalLimitationLegallyBlind" label="Legally Blind" checked={formData.functionalLimitationLegallyBlind} onChange={handleChange}  isReadOnly={isReadOnly} />
                    <CheckboxField field="functionalLimitationDyspnea" label="Dyspnea with minimal exertion" checked={formData.functionalLimitationDyspnea} onChange={handleChange}  isReadOnly={isReadOnly} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Speech-Language Specific Limitations</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <CheckboxField field="functionalLimitationSpeech" label="Speech Production" checked={formData.functionalLimitationSpeech} onChange={handleChange}  isReadOnly={isReadOnly} />
                    <CheckboxField field="functionalLimitationLanguage" label="Language/Communication" checked={formData.functionalLimitationLanguage} onChange={handleChange} />
                    <CheckboxField field="functionalLimitationSwallowing" label="Swallowing/Dysphagia" checked={formData.functionalLimitationSwallowing} onChange={handleChange} />
                    <CheckboxField field="functionalLimitationCognitive" label="Cognitive-Communication" checked={formData.functionalLimitationCognitive} onChange={handleChange}  isReadOnly={isReadOnly} />
                    <CheckboxField field="functionalLimitationVoice" label="Voice" checked={formData.functionalLimitationVoice} onChange={handleChange}  isReadOnly={isReadOnly} />
                    <CheckboxField field="functionalLimitationFluency" label="Fluency" checked={formData.functionalLimitationFluency} onChange={handleChange}  isReadOnly={isReadOnly} />
                    <CheckboxField field="functionalLimitationAAC" label="AAC Needs" checked={formData.functionalLimitationAAC} onChange={handleChange}  isReadOnly={isReadOnly} />
                  </div>
                  {errors.functionalLimitationSpeech && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.functionalLimitationSpeech}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Other Functional Limitations</label>
                  <input type="text" value={formData.functionalLimitationOther} onChange={(e) => handleChange('functionalLimitationOther', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} placeholder="e.g., Ventilator dependent, non-ambulatory, tracheostomy" />
                </div>
              </div>
            </div>
          </div>

          {/* ========== ACTIVITIES PERMITTED ========== */}
          <div className="bg-green-50 rounded-lg border border-green-200">
            <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => toggleSection('activitiesPermitted')}>
              <h4 className="text-md font-medium text-green-900 flex items-center">
                <Activity size={18} className="mr-2 text-green-600" />Activities Permitted (CMS-485 Section 9)
              </h4>
              {collapsedSections.activitiesPermitted ? <ChevronDown className="text-green-900" /> : <ChevronUp className="text-green-900" />}
            </div>
            <div className={collapsedSections.activitiesPermitted ? 'hidden' : 'p-4 border-t border-green-300'}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Activity Level (Check all that apply)</label>
                  <div className="grid grid-cols-2 gap-3">
                    {['Complete bedrest', 'Bed rest with bathroom privileges (BRP)', 'Up as tolerated', 'Independent at home'].map((option) => (
                      <label key={option} className="flex items-center space-x-2">
                        <input type="checkbox" className={`rounded border-gray-300 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                          disabled={isReadOnly}
                          checked={formData.activitiesPermittedLevel.includes(option)}
                          onChange={(e) => {
                            const current = formData.activitiesPermittedLevel || [];
                            const newVals = e.target.checked ? [...current, option] : current.filter(v => v !== option);
                            handleChange('activitiesPermittedLevel', newVals);
                          }} />
                        <span className="text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Special Precautions for ST</label>
                  <textarea value={formData.activitiesPermittedSpecialPrecautions} onChange={(e) => handleChange('activitiesPermittedSpecialPrecautions', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="e.g., Seizure precautions during therapy, aspiration precautions during feeding trials, ventilator precautions" />
                </div>
              </div>
            </div>
          </div>

          {/* ========== HOMEBOUND STATUS ========== */}
          {isHomeHealth && (
            <HomeboundSection
              title="Homebound Status (CMS-485 Section 15)"
              tone="indigo"
              icon={Shield}
              columns={2}
              fields={ST_HOMEBOUND_FIELDS}
              values={formData}
              onChange={handleChange}
              errors={errors}
              readOnly={isReadOnly}
              collapsed={collapsedSections.homebound}
              onToggle={() => toggleSection('homebound')}
            />
          )}

          {/* ========== PHYSICIAN ORDERS AND CERTIFICATION ========== */}
          <div className="bg-blue-50 rounded-lg border border-blue-200">
            <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => toggleSection('physicianOrders')}>
              <h4 className="text-md font-medium text-blue-900 flex items-center">
                <Shield size={18} className="mr-2 text-blue-600" />Physician Orders and Certification (CMS-485 Sections 14, 17)
              </h4>
              {collapsedSections.physicianOrders ? <ChevronDown className="text-blue-900" /> : <ChevronUp className="text-blue-900" />}
            </div>
            <div className={collapsedSections.physicianOrders ? 'hidden' : 'p-4 border-t border-blue-300'}>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Verbal Order Received *</label>
                  <select disabled={isReadOnly} value={formData.verbalOrderReceived} onChange={(e) => handleChange('verbalOrderReceived', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select</option><option value="Yes">Yes</option><option value="No">No</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Verbal Order Date</label>
                  <input type="date" disabled={isReadOnly} value={formData.verbalOrderDate} onChange={(e) => handleChange('verbalOrderDate', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} />
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Verbal Order Received By</label>
                  <input type="text" value={formData.verbalOrderReceivedBy} onChange={(e) => handleChange('verbalOrderReceivedBy', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} placeholder="Clinician name" />
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Written POC Sent Date *</label>
                  <input type="date" disabled={isReadOnly} value={formData.writtenPOCSentDate} onChange={(e) => handleChange('writtenPOCSentDate', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} />
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Method POC Sent *</label>
                  <select disabled={isReadOnly} value={formData.writtenPOCSentMethod} onChange={(e) => handleChange('writtenPOCSentMethod', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select</option><option value="Fax">Fax</option><option value="Secure Email">Secure Email</option><option value="Portal">Portal</option><option value="In-person">In-person</option><option value="Mail">Mail</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Physician Reviewed POC Date</label>
                  <input type="date" value={formData.physicianReceivedPOCDate} onChange={(e) => handleChange('physicianReceivedPOCDate', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md ${errors.physicianReceivedPOCDate ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} />
                  <p className="text-xs text-gray-600 mt-1">Must be within 14 days of verbal order</p>
                  {errors.physicianReceivedPOCDate && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.physicianReceivedPOCDate}</p>}
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Signature Status *</label>
                  <select disabled={isReadOnly} value={formData.physicianSignatureStatus} onChange={(e) => handleChange('physicianSignatureStatus', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select</option><option value="Pending">Pending signature</option><option value="Signed - paper">Signed - paper</option><option value="Signed - electronic">Signed - electronic</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Date Physician Signed POC</label>
                  <input type="date" disabled={isReadOnly} value={formData.physicianSignedDate} onChange={(e) => handleChange('physicianSignedDate', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} />
                </div>
                {/* Phase 19 F26: face-to-face is Medicare content, shown only with the medicareContent flag */}
                {medicareEnabled && (<>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Face-to-Face Required</label>
                  <select disabled={isReadOnly} value={formData.faceToFaceRequired} onChange={(e) => handleChange('faceToFaceRequired', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select</option><option value="Yes - Medicare">Yes - Medicare patient</option><option value="No">No - Medicaid/other payor</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Face-to-Face Date</label>
                  <input type="date" disabled={isReadOnly} value={formData.faceToFaceDate} onChange={(e) => handleChange('faceToFaceDate', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} />
                  <p className="text-xs text-gray-600 mt-1">Within 90 days before or 30 days after SOC</p>
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Face-to-Face Type</label>
                  <select disabled={isReadOnly} value={formData.faceToFaceType} onChange={(e) => handleChange('faceToFaceType', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select</option><option value="In-person visit">In-person visit</option><option value="Telehealth">Telehealth</option><option value="Hospital discharge">Hospital discharge visit</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Face-to-Face Clinician Name</label>
                  <input type="text" value={formData.faceToFaceClinicianName} onChange={(e) => handleChange('faceToFaceClinicianName', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} placeholder="Physician or NPP who performed encounter" />
                </div>
                </>)}
              </div>
            </div>
          </div>

          {/* ========== GOALS (Phase 19 F12: one structure for every discipline) ========== */}
          <GoalsEditor
            discipline="ST"
            goals={formData.goals}
            onChange={(goals) => setFormData(prev => ({ ...prev, goals }))}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={Boolean(collapsedSections.goals)}
            onToggle={() => toggleSection('goals')}
          />

          {/* ========== PEDIATRIC LANGUAGE DEVELOPMENT ========== */}
          {isPediatric && (
            <div className="bg-purple-50 rounded-lg border border-purple-200">
              <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => toggleSection('pediatricLanguage')}>
                <h4 className="text-md font-medium text-purple-900 flex items-center">
                  <Baby size={18} className="mr-2 text-purple-600" />Pediatric Language Development
                </h4>
                {collapsedSections.pediatricLanguage ? <ChevronDown className="text-purple-900" /> : <ChevronUp className="text-purple-900" />}
              </div>
              <div className={collapsedSections.pediatricLanguage ? 'hidden' : 'p-4 border-t border-purple-300'}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Chronological Age</label>
                    <input type="text" value={formData.pediatricChronologicalAge} onChange={(e) => handleChange('pediatricChronologicalAge', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} placeholder="e.g., 2 years 4 months" />
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Adjusted Age (if applicable)</label>
                    <input type="text" value={formData.pediatricAdjustedAge} onChange={(e) => handleChange('pediatricAdjustedAge', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} placeholder="e.g., 1 year 10 months" />
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Receptive Language Age</label>
                    <input type="text" value={formData.pediatricReceptiveLanguageAge} onChange={(e) => handleChange('pediatricReceptiveLanguageAge', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} placeholder="e.g., 12 months" />
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Expressive Language Age</label>
                    <input type="text" value={formData.pediatricExpressiveLanguageAge} onChange={(e) => handleChange('pediatricExpressiveLanguageAge', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} placeholder="e.g., 9 months" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Current Communication Level</label>
                    <select value={formData.pediatricCurrentCommunicationLevel} onChange={(e) => handleChange('pediatricCurrentCommunicationLevel', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select level</option><option value="Pre-intentional">Pre-intentional communication</option><option value="Intentional">Intentional - gestures/vocalizations</option>
                      <option value="Single words">Single words</option><option value="2-word combinations">2-word combinations</option><option value="Phrases/Sentences">Phrases/Sentences</option><option value="AAC user">AAC user</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========== PDN ST CONSIDERATIONS ========== */}
          {isPediatricPDN && (
            <div className="bg-red-50 rounded-lg border border-red-200">
              <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => toggleSection('pdnConsiderations')}>
                <h4 className="text-md font-medium text-red-900 flex items-center">
                  <Heart size={18} className="mr-2 text-red-600" />PDN ST Considerations (Texas Medicaid)
                </h4>
                {collapsedSections.pdnConsiderations ? <ChevronDown className="text-red-900" /> : <ChevronUp className="text-red-900" />}
              </div>
              <div className={collapsedSections.pdnConsiderations ? 'hidden' : 'p-4 border-t border-red-300'}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Tracheostomy Status</label>
                    <select disabled={isReadOnly} value={formData.pdnTracheostomyStatus} onChange={(e) => handleChange('pdnTracheostomyStatus', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select</option><option value="No tracheostomy">No tracheostomy</option><option value="Trach present">Tracheostomy present - affects voicing</option>
                      <option value="Speaking valve candidate">Tracheostomy - speaking valve candidate</option><option value="Uses speaking valve">Tracheostomy - uses speaking valve</option>
                    </select>
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Speaking Valve Status</label>
                    <select disabled={isReadOnly} value={formData.pdnSpeakingValveStatus} onChange={(e) => handleChange('pdnSpeakingValveStatus', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select</option><option value="N/A">N/A - no tracheostomy</option><option value="Using successfully">Using Passy-Muir valve successfully</option>
                      <option value="Trialing">Trialing speaking valve</option><option value="Not candidate">Not appropriate candidate</option>
                    </select>
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Ventilator Status</label>
                    <select disabled={isReadOnly} value={formData.pdnVentilatorStatus} onChange={(e) => handleChange('pdnVentilatorStatus', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select</option><option value="No ventilator">No ventilator</option><option value="Vent dependent">Ventilator dependent - affects communication</option>
                      <option value="Nocturnal only">Ventilator dependent - nocturnal only</option><option value="Weaning">Weaning in progress</option>
                    </select>
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Medical Stability for ST *</label>
                    <select value={formData.pdnMedicalStabilityForST} onChange={(e) => handleChange('pdnMedicalStabilityForST', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border rounded-md ${errors.pdnMedicalStabilityForST ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select</option><option value="Stable - full activities">Medically stable - full ST activities</option><option value="Stable with precautions">Stable with precautions</option>
                      <option value="Fragile - bedside only">Medically fragile - bedside ST only</option><option value="Unstable - on hold">Unstable - ST on hold</option>
                    </select>
                    {errors.pdnMedicalStabilityForST && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.pdnMedicalStabilityForST}</p>}
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Communication with Tracheostomy</label>
                    <textarea value={formData.pdnCommunicationWithTracheostomy} onChange={(e) => handleChange('pdnCommunicationWithTracheostomy', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="Describe how patient communicates with trach" />
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">ST Session Precautions</label>
                    <textarea value={formData.pdnSTSessionPrecautions} onChange={(e) => handleChange('pdnSTSessionPrecautions', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="e.g., Monitor SpO2, suction available, upright positioning" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========== SPEECH THERAPY INTERVENTIONS ========== */}
          <div className="bg-purple-50 rounded-lg border border-purple-200">
            <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => toggleSection('interventions')}>
              <h4 className="text-md font-medium text-purple-900 flex items-center">
                <Brain size={18} className="mr-2 text-purple-600" />Speech Therapy Interventions
              </h4>
              {collapsedSections.interventions ? <ChevronDown className="text-purple-900" /> : <ChevronUp className="text-purple-900" />}
            </div>
            <div className={collapsedSections.interventions ? 'hidden' : 'p-4 border-t border-purple-300'}>
              <div className="space-y-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Speech-Language Interventions *</label>
                  <textarea value={formData.interventionSpeechLanguage} onChange={(e) => handleChange('interventionSpeechLanguage', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md ${errors.interventionSpeechLanguage ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3}
                    placeholder="Language therapy, articulation therapy, pragmatic language intervention, etc." />
                  {errors.interventionSpeechLanguage && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.interventionSpeechLanguage}</p>}
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Swallowing/Dysphagia Interventions</label>
                  <textarea value={formData.interventionSwallowingDysphagia} onChange={(e) => handleChange('interventionSwallowingDysphagia', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={3} placeholder="Oral motor exercises, swallowing strategies, diet modifications, etc." />
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Other Interventions</label>
                  <textarea value={formData.interventionOther} onChange={(e) => handleChange('interventionOther', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="Voice therapy, fluency techniques, AAC training, etc." />
                </div>
              </div>
            </div>
          </div>

          {/* ========== SWALLOWING/FEEDING PLAN ========== */}
          {isPediatricPDN && (
            <div className="bg-orange-50 rounded-lg border border-orange-200">
              <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => toggleSection('swallowing')}>
                <h4 className="text-md font-medium text-orange-900 flex items-center">
                  <Utensils size={18} className="mr-2 text-orange-600" />Swallowing/Feeding Plan
                </h4>
                {collapsedSections.swallowing ? <ChevronDown className="text-orange-900" /> : <ChevronUp className="text-orange-900" />}
              </div>
              <div className={collapsedSections.swallowing ? 'hidden' : 'p-4 border-t border-orange-300'}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Current Feeding Method</label>
                    <select disabled={isReadOnly} value={formData.pdnCurrentFeedingMethod} onChange={(e) => handleChange('pdnCurrentFeedingMethod', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select</option><option value="Oral only">Oral only</option><option value="G-tube with oral trials">G-tube with oral trials</option><option value="G-tube only">G-tube only - no oral</option><option value="NPO">NPO</option>
                    </select>
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Oral Feeding Status</label>
                    <select disabled={isReadOnly} value={formData.pdnOralFeedingStatus} onChange={(e) => handleChange('pdnOralFeedingStatus', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select</option><option value="Safe">Safe for oral feeding - age appropriate</option><option value="Safe with modifications">Safe with modifications/supervision</option>
                      <option value="Limited trials">Limited oral trials - therapeutic only</option><option value="NPO - aspiration">NPO - aspiration risk</option>
                    </select>
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Diet Texture</label>
                    <select disabled={isReadOnly} value={formData.pdnDietTexture} onChange={(e) => handleChange('pdnDietTexture', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select</option><option value="Regular">Regular/Age-appropriate</option><option value="Soft">Soft solids</option><option value="Mashed">Mashed/Ground</option><option value="Pureed">Pureed</option><option value="NPO">NPO</option>
                    </select>
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Liquid Consistency</label>
                    <select disabled={isReadOnly} value={formData.pdnLiquidConsistency} onChange={(e) => handleChange('pdnLiquidConsistency', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select</option><option value="Thin">Thin liquids</option><option value="Level 1">Slightly thick (Level 1)</option><option value="Level 2">Mildly thick (Level 2)</option>
                      <option value="Level 3">Moderately thick (Level 3)</option><option value="No oral">No oral liquids</option>
                    </select>
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Aspiration Risk Level</label>
                    <select disabled={isReadOnly} value={formData.pdnAspirationRisk} onChange={(e) => handleChange('pdnAspirationRisk', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select</option><option value="Low">Low risk</option><option value="Moderate">Moderate risk</option><option value="High">High risk - modified diet</option><option value="Very high">Very high risk - NPO recommended</option>
                    </select>
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Swallowing Precautions</label>
                    <textarea value={formData.pdnSwallowingPrecautions} onChange={(e) => handleChange('pdnSwallowingPrecautions', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="e.g., Upright positioning, pacing, small bites, supervision" />
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Swallowing Interventions</label>
                    <textarea value={formData.pdnSwallowingIntervention} onChange={(e) => handleChange('pdnSwallowingIntervention', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="Specific swallowing therapy techniques" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========== AAC PLAN ========== */}
          {isPediatricPDN && (
            <div className="bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => toggleSection('aac')}>
                <h4 className="text-md font-medium text-yellow-900 flex items-center">
                  <Volume2 size={18} className="mr-2 text-yellow-600" />AAC (Augmentative/Alternative Communication) Plan
                </h4>
                {collapsedSections.aac ? <ChevronDown className="text-yellow-900" /> : <ChevronUp className="text-yellow-900" />}
              </div>
              <div className={collapsedSections.aac ? 'hidden' : 'p-4 border-t border-yellow-300'}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">AAC Status</label>
                    <select disabled={isReadOnly} value={formData.pdnAACStatus} onChange={(e) => handleChange('pdnAACStatus', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select</option><option value="No AAC needed">No AAC needed - verbal communication</option><option value="Evaluation recommended">AAC evaluation recommended</option>
                      <option value="Low-tech in use">Low-tech AAC in use</option><option value="High-tech in use">High-tech AAC in use</option><option value="Training in progress">AAC training in progress</option>
                    </select>
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Current AAC System</label>
                    <textarea value={formData.pdnCurrentAACSystem} onChange={(e) => handleChange('pdnCurrentAACSystem', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="e.g., Communication board, PODD book, TouchChat on iPad" />
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">AAC Access Method</label>
                    <select disabled={isReadOnly} value={formData.pdnAACAccessMethod} onChange={(e) => handleChange('pdnAACAccessMethod', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select</option><option value="N/A">N/A - no AAC</option><option value="Direct selection - finger">Direct selection - finger</option>
                      <option value="Eye gaze">Direct selection - eye gaze</option><option value="Switch scanning">Switch scanning</option><option value="Partner-assisted">Partner-assisted scanning</option>
                    </select>
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">AAC Training Needs</label>
                    <textarea value={formData.pdnAACTrainingNeeds} onChange={(e) => handleChange('pdnAACTrainingNeeds', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="Training needs for patient and caregivers" />
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">AAC Interventions</label>
                    <textarea value={formData.pdnAACIntervention} onChange={(e) => handleChange('pdnAACIntervention', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="AAC implementation strategies and training plan" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========== CAREGIVER ST TRAINING ========== */}
          {isPediatricPDN && (
            <div className="bg-teal-50 rounded-lg border border-teal-200">
              <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => toggleSection('caregiverTraining')}>
                <h4 className="text-md font-medium text-teal-900 flex items-center">
                  <User size={18} className="mr-2 text-teal-600" />Caregiver ST Training Plan
                </h4>
                {collapsedSections.caregiverTraining ? <ChevronDown className="text-teal-900" /> : <ChevronUp className="text-teal-900" />}
              </div>
              <div className={collapsedSections.caregiverTraining ? 'hidden' : 'p-4 border-t border-teal-300'}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Primary Caregiver for ST Training</label>
                    <input type="text" value={formData.pdnSTCaregiverName} onChange={(e) => handleChange('pdnSTCaregiverName', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} placeholder="Name of caregiver to train" />
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">ST Training Status</label>
                    <select disabled={isReadOnly} value={formData.pdnSTCaregiverTrainingStatus} onChange={(e) => handleChange('pdnSTCaregiverTrainingStatus', e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                      <option value="">Select</option><option value="Fully trained">Fully trained - all competencies verified</option><option value="Partially trained">Partially trained</option>
                      <option value="Training in progress">Training in progress</option><option value="Not trained">Not trained - training scheduled</option>
                    </select>
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Verified ST Competencies</label>
                    <textarea value={formData.pdnSTCaregiverCompetencies} onChange={(e) => handleChange('pdnSTCaregiverCompetencies', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="e.g., Language stimulation, feeding strategies, AAC use, aspiration precautions" />
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Language Stimulation Training</label>
                    <textarea value={formData.pdnLanguageStimulationTraining} onChange={(e) => handleChange('pdnLanguageStimulationTraining', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="Training on naturalistic language stimulation" />
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Feeding Strategy Training</label>
                    <textarea value={formData.pdnFeedingStrategyTraining} onChange={(e) => handleChange('pdnFeedingStrategyTraining', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="Safe feeding techniques, positioning, texture progression" />
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">AAC Use Training</label>
                    <textarea value={formData.pdnAACUseTraining} onChange={(e) => handleChange('pdnAACUseTraining', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="AAC modeling, prompting, communication opportunities" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========== ST VISIT FREQUENCY AND DURATION ========== */}
          <div className="bg-orange-50 rounded-lg border border-orange-200">
            <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => toggleSection('frequency')}>
              <h4 className="text-md font-medium text-orange-900 flex items-center">
                <Clock size={18} className="mr-2 text-orange-600" />ST Visit Frequency and Duration (CMS-485 Section 12)
              </h4>
              {collapsedSections.frequency ? <ChevronDown className="text-orange-900" /> : <ChevronUp className="text-orange-900" />}
            </div>
            <div className={collapsedSections.frequency ? 'hidden' : 'p-4 border-t border-orange-300'}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Visits Per Week *</label>
                  <input type="number" value={formData.stVisitsPerWeek} onChange={(e) => handleChange('stVisitsPerWeek', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md ${errors.stVisitsPerWeek ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} min="1" max="7" placeholder="e.g., 2" />
                  {errors.stVisitsPerWeek && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.stVisitsPerWeek}</p>}
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Minutes Per Visit *</label>
                  <select value={formData.stMinutesPerVisit} onChange={(e) => handleChange('stMinutesPerVisit', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md ${errors.stMinutesPerVisit ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select</option><option value="15">15</option><option value="30">30</option><option value="45">45</option><option value="60">60</option><option value="75">75</option><option value="90">90</option>
                  </select>
                  {errors.stMinutesPerVisit && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.stMinutesPerVisit}</p>}
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Total Hours Per Week</label>
                  <input type="text" value={formData.stTotalHoursPerWeek} readOnly className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600" placeholder="Auto-calculated" />
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input type="date" value={formData.stStartDate} onChange={(e) => handleChange('stStartDate', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md ${errors.stStartDate ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} />
                  {errors.stStartDate && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.stStartDate}</p>}
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Anticipated End Date *</label>
                  <input type="date" value={formData.stAnticipatedEndDate} onChange={(e) => handleChange('stAnticipatedEndDate', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md ${errors.stAnticipatedEndDate ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} />
                  {errors.stAnticipatedEndDate && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.stAnticipatedEndDate}</p>}
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Total Days</label>
                  <input type="text" value={formData.stTotalDays} readOnly className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600" placeholder="Auto-calculated" />
                </div>
                <div className="md:col-span-3"><label className="block text-sm font-medium text-gray-700 mb-1">Certification Period *</label>
                  <select value={formData.stCertificationPeriod} onChange={(e) => handleChange('stCertificationPeriod', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md ${errors.stCertificationPeriod ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}>
                    <option value="">Select</option><option value="60 days">60 days</option><option value="90 days">90 days</option><option value="120 days">120 days</option><option value="180 days">180 days</option>
                  </select>
                  {errors.stCertificationPeriod && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.stCertificationPeriod}</p>}
                </div>
                <div className="md:col-span-3"><label className="block text-sm font-medium text-gray-700 mb-1">Frequency Justification {isPediatricPDN && '*'}</label>
                  <textarea value={formData.stFrequencyJustification} onChange={(e) => handleChange('stFrequencyJustification', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border rounded-md ${errors.stFrequencyJustification ? 'border-red-500' : 'border-gray-300'} ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2}
                    placeholder="Justify frequency based on medical condition, communication/swallowing needs, safety concerns, expected progress rate" />
                  {errors.stFrequencyJustification && <p className="text-red-500 text-sm mt-1"><AlertCircle size={16} className="inline mr-1" />{errors.stFrequencyJustification}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* ========== DME AND EQUIPMENT ========== */}
          <div className="bg-gray-50 rounded-lg border border-gray-200">
            <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => toggleSection('dme')}>
              <h4 className="text-md font-medium text-gray-900 flex items-center">
                <Truck size={18} className="mr-2 text-gray-600" />DME and Equipment
              </h4>
              {collapsedSections.dme ? <ChevronDown className="text-gray-900" /> : <ChevronUp className="text-gray-900" />}
            </div>
            <div className={collapsedSections.dme ? 'hidden' : 'p-4 border-t border-gray-300'}>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                <CheckboxField field="dmeAACDevice" label="AAC Device" checked={formData.dmeAACDevice} onChange={handleChange}  isReadOnly={isReadOnly} />
                <CheckboxField field="dmeCommunicationBoard" label="Communication Board" checked={formData.dmeCommunicationBoard} onChange={handleChange}  isReadOnly={isReadOnly} />
                <CheckboxField field="dmeSpeakingValve" label="Speaking Valve" checked={formData.dmeSpeakingValve} onChange={handleChange}  isReadOnly={isReadOnly} />
                <CheckboxField field="dmeThickeningAgents" label="Thickening Agents" checked={formData.dmeThickeningAgents} onChange={handleChange}  isReadOnly={isReadOnly} />
                <CheckboxField field="dmeAdaptedUtensils" label="Adapted Utensils" checked={formData.dmeAdaptedUtensils} onChange={handleChange}  isReadOnly={isReadOnly} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">AAC Device Type</label>
                  <input type="text" value={formData.dmeAACDeviceType} onChange={(e) => handleChange('dmeAACDeviceType', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} placeholder="e.g., TouchChat on iPad, GoTalk" />
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Speaking Valve Type</label>
                  <input type="text" value={formData.dmeSpeakingValveType} onChange={(e) => handleChange('dmeSpeakingValveType', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} placeholder="e.g., Passy-Muir PMV007" />
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Other DME</label>
                  <input type="text" value={formData.dmeOther} onChange={(e) => handleChange('dmeOther', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} placeholder="Other equipment" />
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">DME Supplier</label>
                  <input type="text" value={formData.dmeSupplier} onChange={(e) => handleChange('dmeSupplier', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} placeholder="Supplier name" />
                </div>
              </div>
            </div>
          </div>

          {/* ========== SAFETY MEASURES ========== */}
          <div className="bg-red-50 rounded-lg border border-red-200">
            <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => toggleSection('safety')}>
              <h4 className="text-md font-medium text-red-900 flex items-center">
                <Shield size={18} className="mr-2 text-red-600" />Safety Measures
              </h4>
              {collapsedSections.safety ? <ChevronDown className="text-red-900" /> : <ChevronUp className="text-red-900" />}
            </div>
            <div className={collapsedSections.safety ? 'hidden' : 'p-4 border-t border-red-300'}>
              <div className="space-y-3">
                <CheckboxField field="safetyAspirationPrecautions" label="Aspiration Precautions" checked={formData.safetyAspirationPrecautions} onChange={handleChange}  isReadOnly={isReadOnly} />
                {formData.safetyAspirationPrecautions && (
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Aspiration Precautions Details</label>
                    <textarea value={formData.safetyAspirationPrecautionsDetails} onChange={(e) => handleChange('safetyAspirationPrecautionsDetails', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} />
                  </div>
                )}
                <CheckboxField field="safetySeizurePrecautions" label="Seizure Precautions" checked={formData.safetySeizurePrecautions} onChange={handleChange}  isReadOnly={isReadOnly} />
                <CheckboxField field="safetyTracheostomyPrecautions" label="Tracheostomy Precautions" checked={formData.safetyTracheostomyPrecautions} onChange={handleChange}  isReadOnly={isReadOnly} />
                <CheckboxField field="safetySupervisedFeeding" label="Supervised Feeding Required" checked={formData.safetySupervisedFeeding} onChange={handleChange}  isReadOnly={isReadOnly} />
                <CheckboxField field="safetyEmergencyPlanReviewed" label="Emergency Plan Reviewed with Caregiver" checked={formData.safetyEmergencyPlanReviewed} onChange={handleChange}  isReadOnly={isReadOnly} />
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Other Safety Measures</label>
                  <textarea value={formData.safetyOther} onChange={(e) => handleChange('safetyOther', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} />
                </div>
              </div>
            </div>
          </div>

          {/* ========== COORDINATION OF CARE ========== */}
          <div className="bg-yellow-50 rounded-lg border border-yellow-200">
            <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => toggleSection('coordination')}>
              <h4 className="text-md font-medium text-yellow-900 flex items-center">
                <ClipboardList size={18} className="mr-2 text-yellow-600" />Coordination of Care
              </h4>
              {collapsedSections.coordination ? <ChevronDown className="text-yellow-900" /> : <ChevronUp className="text-yellow-900" />}
            </div>
            <div className={collapsedSections.coordination ? 'hidden' : 'p-4 border-t border-yellow-300'}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Coordination with Nursing</label>
                  <textarea value={formData.coordinationWithNursing} onChange={(e) => handleChange('coordinationWithNursing', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="ST activities for nursing carryover" />
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Coordination with PT</label>
                  <textarea value={formData.coordinationWithPT} onChange={(e) => handleChange('coordinationWithPT', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="Positioning, mobility considerations" />
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Coordination with OT</label>
                  <textarea value={formData.coordinationWithOT} onChange={(e) => handleChange('coordinationWithOT', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="Feeding coordination, sensory needs" />
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Coordination with ENT</label>
                  <textarea value={formData.coordinationWithENT} onChange={(e) => handleChange('coordinationWithENT', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="Trach management, speaking valve" />
                </div>
                {isPediatric && (
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Coordination with School/ECI</label>
                    <textarea value={formData.coordinationWithSchool} onChange={(e) => handleChange('coordinationWithSchool', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="IEP coordination, ECI transition" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ========== DISCHARGE PLANNING ========== */}
          <div className="bg-green-50 rounded-lg border border-green-200">
            <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => toggleSection('discharge')}>
              <h4 className="text-md font-medium text-green-900 flex items-center">
                <Home size={18} className="mr-2 text-green-600" />Discharge Planning
              </h4>
              {collapsedSections.discharge ? <ChevronDown className="text-green-900" /> : <ChevronUp className="text-green-900" />}
            </div>
            <div className={collapsedSections.discharge ? 'hidden' : 'p-4 border-t border-green-300'}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Discharge Goals</label>
                  <textarea value={formData.dischargeGoals} onChange={(e) => handleChange('dischargeGoals', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="Communication/swallowing goals for discharge" />
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Discharge Criteria</label>
                  <textarea value={formData.dischargeCriteria} onChange={(e) => handleChange('dischargeCriteria', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="Specific criteria to meet for discharge" />
                </div>
                {isPediatric && (
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">School Transition Plan</label>
                    <textarea value={formData.schoolTransitionPlan} onChange={(e) => handleChange('schoolTransitionPlan', e.target.value)}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="IEP transition, school-based ST handoff" />
                  </div>
                )}
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Follow-Up Recommendations</label>
                  <textarea value={formData.followUpRecommendations} onChange={(e) => handleChange('followUpRecommendations', e.target.value)}
                    disabled={isReadOnly}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={2} placeholder="Ongoing ST needs, referrals" />
                </div>
              </div>
            </div>
          </div>

          {/* ========== MEDICAL NECESSITY STATEMENT ========== */}
          <MedicalNecessitySection
            title="Medical Necessity Statement (CMS-485 Section 16)"
            icon={Stethoscope}
            fields={ST_MEDICAL_NECESSITY_FIELDS}
            values={formData}
            onChange={handleChange}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={collapsedSections.medicalNecessity}
            onToggle={() => toggleSection('medicalNecessity')}
          />

          {/* ========== PROVIDER INFORMATION ========== */}
          <ProviderSection
            tone="pink"
            keys={['providerName', 'providerCredentials', 'providerNPI', 'supervisingPhysician', 'supervisingPhysicianNPI', 'dateCreated']}
            overrides={{ providerCredentials: { placeholder: 'e.g., CCC-SLP, MS' } }}
            values={formData}
            onChange={handleChange}
            errors={errors}
            readOnly={isReadOnly}
            collapsed={collapsedSections.provider}
            onToggle={() => toggleSection('provider')}
          />

          {/* ========== ADDITIONAL NOTES ========== */}
          <div className="bg-gray-50 rounded-lg border border-gray-200">
            <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => toggleSection('additionalNotes')}>
              <h4 className="text-md font-medium text-gray-900 flex items-center">
                <ClipboardList size={18} className="mr-2 text-gray-600" />Additional Notes
              </h4>
              {collapsedSections.additionalNotes ? <ChevronDown className="text-gray-900" /> : <ChevronUp className="text-gray-900" />}
            </div>
            <div className={collapsedSections.additionalNotes ? 'hidden' : 'p-4 border-t border-gray-300'}>
              <textarea value={formData.additionalNotes} onChange={(e) => handleChange('additionalNotes', e.target.value)}
                disabled={isReadOnly}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md ${isReadOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`} rows={4} placeholder="Add any additional notes here" />
            </div>
          </div>
      </div>
      {draft.dialog}
    </FormPageLayout>
  );
};

export default STCarePlanForm;

// ==================== END OF PART 6 - FILE COMPLETE ====================
